import type { OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import { MatTooltip, MatTooltipModule } from '@angular/material/tooltip';
import { Clipboard } from '@freelancer/clipboard';
import { ShareServiceHelper } from '@freelancer/share-helper';
import { ButtonColor, ButtonSize } from '../button';
import { ButtonComponent } from '../button/button.component';
import { IconSize } from '../icon';
import { IconModule } from '../icon/icon.module';

@Component({
  standalone: true,
  selector: 'fl-share-link',
  imports: [ButtonComponent, IconModule, MatTooltipModule],
  template: `
    <div
      #copiedTooltip="matTooltip"
      i18n-matTooltip="Share link copied tooltip"
      matTooltip="Copied!"
      [matTooltipPosition]="'above'"
    ></div>
    <div
      #copyTooltip="matTooltip"
      class="CopyText"
      i18n-matTooltip="Share link copy tooltip"
      matTooltip="Copy to clipboard"
      [matTooltipPosition]="'above'"
      (click)="copyClipBoard($event)"
      (mouseleave)="resetToolTip()"
    >
      <p class="Text text-xsmall text-left">
        {{ displayLink }}
      </p>

      <fl-button
        [color]="ButtonColor.TEXT_DARK"
        [size]="ButtonSize.MID"
      >
        <div class="Button">
          <fl-icon
            class="mr-xxsmall"
            [name]="'ui-multifile'"
            [useIconFont]="true"
            [fillIconFont]="true"
            [size]="IconSize.SMALL"
          ></fl-icon>
          <p i18n="Copy">Copy</p>
        </div>
      </fl-button>
    </div>
  `,
  styleUrls: ['./share-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ShareLinkComponent implements OnInit, OnChanges {
  private clipboard = inject(Clipboard);
  private shareServiceHelper = inject(ShareServiceHelper);

  ButtonColor = ButtonColor;
  ButtonSize = ButtonSize;
  IconSize = IconSize;

  @ViewChild('copyTooltip') copyTooltip: MatTooltip;
  @ViewChild('copiedTooltip') copiedTooltip: MatTooltip;

  @Input() excludeShareTracking = false;
  @Input() hideProtocol = false;
  @Input() shareUrl: string;

  @Output() onCopyLink = new EventEmitter<void>();

  link: string;
  displayLink: string;

  ngOnInit(): void {
    this.setDisplayLink();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('shareUrl' in changes || 'excludeShareTracking' in changes) {
      this.setDisplayLink();
    }
  }

  setDisplayLink(): void {
    this.link = this.shareServiceHelper.buildShareUrl(
      this.shareUrl,
      this.excludeShareTracking,
    );

    const url = new URL(this.link);
    this.displayLink = this.hideProtocol
      ? `${url.host.replace('www.', '')}${url.pathname}${url.search ?? ''}`
      : this.link;
  }

  copyClipBoard(event: Event): void {
    this.clipboard.copy(this.link);
    this.copyTooltip.hide();
    this.copiedTooltip.show();
    this.onCopyLink.emit();
  }

  resetToolTip(): void {
    this.copiedTooltip.hide();
  }
}
